/**
 * `gallery` — monday's `ItemsGalleryBoardView`, the **Cards** view
 * (`specs/M4-V7-gallery.md`).
 *
 * Four exist on this account and monday no longer offers the kind in its add-view gallery
 * (`+` lists *File gallery*, which is the unrelated `FilesGalleryBoardView` widget). The kind
 * is therefore registered under *Explore more views* rather than *Board views* — ours lists
 * what monday's `+` lists, and this is not on it (`[V7§8]`).
 */
import { z } from 'zod';
import { defineViewType, normaliseOverDefaults } from '../types.js';
import { mondayShapedSettings } from '../from-monday.js';
import { ALL_COLUMNS_SELECTION, columnSelectionSchema } from '../column-selection.js';
// The kind's own monday reader, so `normaliseSettings` can recognise an imported blob
// (P-025). Re-exported below as well, which is how every caller outside has always had it.
import { gallerySettingsFromMonday } from './from-monday.js';
export * from './from-monday.js';
export * from './to-monday.js';
export const GALLERY_MONDAY_KEYS = new Set(['cover_column', 'card_columns']);
export const galleryViewSettingsSchema = z.object({
    coverColumnId: z.string().nullable(),
    cardColumns: columnSelectionSchema,
    cardSize: z.enum(['small', 'medium', 'large']),
    mondayKeys: z.array(z.string()),
    mondayExtras: z.record(z.string(), z.unknown()),
});
export const DEFAULT_GALLERY_VIEW_SETTINGS = {
    coverColumnId: null,
    cardColumns: ALL_COLUMNS_SELECTION,
    cardSize: 'medium',
    mondayKeys: [],
    mondayExtras: {},
};
/** Measured on the two production Cards views (`[V7§2.1]`); `medium` is monday's own 330. */
export const GALLERY_METRICS = {
    cardWidth: { small: 240, medium: 330, large: 420 },
    /** Measured: a 330 px card on a 357 px pitch, three across a 1089 px board area. */
    gridGap: 27,
    coverHeight: 150,
    cardHeaderHeight: 64,
    cardCellHeight: 34,
    cardCellGap: 8,
    cardPadding: 8,
    cardPaddingBottom: 4,
};
/**
 * Measured **412** for the production card with eight columns and no cover
 * (`[V7§2.1]`): the cover band, the 64 px header, `8 + 42·n` of cell rows and 4 px of
 * bottom padding.
 */
export function galleryCardHeight(cellCount, hasCover) {
    const pitch = GALLERY_METRICS.cardCellHeight + GALLERY_METRICS.cardCellGap;
    const cells = cellCount > 0 ? pitch * cellCount + GALLERY_METRICS.cardPadding : 0;
    return ((hasCover ? GALLERY_METRICS.coverHeight : 0) +
        GALLERY_METRICS.cardHeaderHeight +
        cells +
        GALLERY_METRICS.cardPaddingBottom);
}
export const galleryView = defineViewType({
    id: 'gallery',
    title: 'Cards',
    mondayViewType: 'ItemsGalleryBoardView',
    kind: 'APP',
    settingsSchema: galleryViewSettingsSchema,
    defaultSettings: DEFAULT_GALLERY_VIEW_SETTINGS,
    normaliseSettings: (input) => normaliseOverDefaults('gallery', galleryViewSettingsSchema, DEFAULT_GALLERY_VIEW_SETTINGS, mondayShapedSettings(input, DEFAULT_GALLERY_VIEW_SETTINGS, gallerySettingsFromMonday)),
    requiredColumnTypes: [],
    supportsFilters: true,
    supportsSort: true,
    supportsGroupBy: false,
    canBeDefault: true,
    implemented: true,
    plannedIn: null,
    gallery: { order: 10, section: 'explore' },
});
//# sourceMappingURL=index.js.map